import React, { useEffect, useMemo, useReducer, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  BlackjackGamePhase,
  initialBlackjackState,
  blackjackReducer,
} from "./DealerJackReducer";
import { InGameViewProps } from "views/GamePage";
import RuleModal from "components/common/RuleModal";
import {
  BlackjackSocketService,
  DealerJackSocketService,
} from "./DealerJackSocketService";
import UserSession from "network/UserSession";
import Chatbox from "components/Chatbox";
import ProductGameHeader from "../ProductGameHeader";
import ChatFloatButton from "components/common/ChatFloatButton";
import ChatModal from "components/common/ChatModal";
import DealerJackPlayerList from "./DealerJackPlayerList";
import "sass/views/dealerjack/DealerJackView.scss";

const DealerJackView: React.FC<InGameViewProps> = ({
  product,
  room,
  messages,
  setMessages,
  onReturnToWaitingRoom,
}) => {
  const { t } = useTranslation();
  const [gameState, dispatch] = useReducer(
    blackjackReducer,
    initialBlackjackState
  );
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isUserListOpen, setIsUserListOpen] = useState(false);
  const [isBettingModalOpen, setIsBettingModalOpen] = useState(false);
  const [betAmount, setBetAmount] = useState(5); // Initialize with minimum bet amount
  const [socketService, setSocketService] =
    useState<DealerJackSocketService | null>(null);
  const [actionNotification, setActionNotification] = useState<{
    message: string;
    show: boolean;
  }>({ message: "", show: false });
  const [dealerCardRevealIndex, setDealerCardRevealIndex] = useState<number>(2); // Initially show 2 cards
  const [showDealerTotal, setShowDealerTotal] = useState<boolean>(false);

  // Track action availability
  const [hasPlayerActed, setHasPlayerActed] = useState(false);

  // Double down modal state
  const [isDoubleDownModalOpen, setIsDoubleDownModalOpen] = useState(false);
  const [doubleDownInfo, setDoubleDownInfo] = useState<{
    currentBet: number;
    additionalBet: number;
    newTotalBet: number;
  } | null>(null);

  // Surrender modal state
  const [isSurrenderModalOpen, setIsSurrenderModalOpen] = useState(false);
  const [surrenderInfo, setSurrenderInfo] = useState<{
    currentBet: number;
    returnAmount: number;
  } | null>(null);

  const dealerAnimationRef = useRef<{
    startDelay: NodeJS.Timeout | null;
    revealTimeouts: NodeJS.Timeout[];
  }>({ startDelay: null, revealTimeouts: [] });

  const myId = UserSession.getInstance().getUserId();

  // Optimize player lookup with useMemo (following RoleDistributor pattern)
  const myPlayer = useMemo(
    () => gameState.players.find((p) => p.id === myId),
    [gameState.players, myId]
  );

  // Initialize socket service
  useEffect(() => {
    const service = new BlackjackSocketService(dispatch);
    service.initializeListeners();
    setSocketService(service);

    // Add action completion notification listener
    const handlePlayerActionCompleted = (data: any) => {
      const message = `${data.playerName}${t(
        "Dealerjack.playerActionCompleted"
      )} (${t("Dealerjack.remainingPlayers")}: ${data.remainingPlayers}${t(
        "Dealerjack.players"
      )})`;

      setActionNotification({ message, show: true });

      // 3초 후 알림 숨기기
      setTimeout(() => {
        setActionNotification((prev) => ({ ...prev, show: false }));
      }, 3000);
    };

    // 소켓 이벤트 리스너 등록
    service.addCustomListener(
      "dealerjack:player_action_completed",
      handlePlayerActionCompleted
    );

    return () => {
      service.removeCustomListener(
        "dealerjack:player_action_completed",
        handlePlayerActionCompleted
      );
      service.cleanup();
    };
  }, []);

  // 게임 설정이 변경될 때 베팅액 조정
  useEffect(() => {
    const minBet = gameState.gameSettings.minBet || 5;
    if (betAmount < minBet) {
      setBetAmount(minBet);
    }
  }, [gameState.gameSettings.minBet, betAmount]);

  // 플레이어 턴 시작 시 액션 상태 리셋
  useEffect(() => {
    if (gameState.phase === BlackjackGamePhase.PLAYER_TURN) {
      setHasPlayerActed(false);
    }
  }, [gameState.phase]);

  // 라운드 시작 시 액션 상태 리셋
  useEffect(() => {
    if (gameState.phase === BlackjackGamePhase.BETTING) {
      setHasPlayerActed(false);
    }
  }, [gameState.currentRound, gameState.phase]);

  // 베팅 모달 자동 열기/닫기 및 자동 올인 처리 (라운드 시작시에만)
  useEffect(() => {
    if (gameState.phase === BlackjackGamePhase.BETTING) {
      const myPlayerChips = myPlayer?.chips || 0;
      const minBet = gameState.gameSettings.minBet || 5;
      const isEliminated = myPlayer?.isEliminated || false;
      const hasAlreadyBet = myPlayer?.currentBet && myPlayer.currentBet > 0;

      if (isEliminated) {
        // 파산된 플레이어는 베팅 모달 열지 않음
        setIsBettingModalOpen(false);
      } else if (
        !hasAlreadyBet &&
        myPlayerChips > 0 &&
        myPlayerChips <= minBet
      ) {
        // 아직 베팅하지 않았고 minBet 이하 칩 보유자는 자동 올인 처리 (라운드 시작시에만)
        setIsBettingModalOpen(false);
        setBetAmount(myPlayerChips);

        // 자동 올인 실행
        setTimeout(() => {
          if (socketService && myPlayer && myPlayer.currentBet === 0) {
            socketService.placeBet(myPlayerChips);
            dispatch({ type: "SET_MY_BET", amount: myPlayerChips });
          }
        }, 100); // 짧은 딜레이로 상태 안정화
      } else if (!hasAlreadyBet && myPlayerChips >= minBet) {
        // 아직 베팅하지 않았고 정상 베팅 가능한 플레이어만 모달 열기
        setIsBettingModalOpen(true);
      } else {
        // 이미 베팅한 경우 모달 닫기
        setIsBettingModalOpen(false);
      }
    } else {
      // 베팅 턴이 끝나면 자동으로 모달 닫기
      setIsBettingModalOpen(false);
    }
  }, [gameState.phase, gameState.currentRound]);

  // 파산된 플레이어 자동 스탠드 처리
  useEffect(() => {
    if (
      gameState.phase === BlackjackGamePhase.PLAYER_TURN &&
      myPlayer?.isEliminated &&
      !myPlayer.actionCompleted &&
      socketService
    ) {
      // 파산된 플레이어는 자동으로 스탠드 처리
      setTimeout(() => {
        if (myPlayer?.isEliminated && !myPlayer.actionCompleted) {
          socketService.requestStand();
        }
      }, 200); // 짧은 딜레이로 다른 상태 업데이트 후 실행
    }
  }, [
    gameState.phase,
    myPlayer?.isEliminated,
    myPlayer?.actionCompleted,
    socketService,
  ]);

  // 딜러 턴 카드 순차 오픈 애니메이션
  useEffect(() => {
    // 이전 타이머 정리
    const clearAllTimeouts = () => {
      if (dealerAnimationRef.current.startDelay) {
        clearTimeout(dealerAnimationRef.current.startDelay);
        dealerAnimationRef.current.startDelay = null;
      }
      dealerAnimationRef.current.revealTimeouts.forEach((timeout) =>
        clearTimeout(timeout)
      );
      dealerAnimationRef.current.revealTimeouts = [];
    };

    clearAllTimeouts();

    if (
      gameState.phase === BlackjackGamePhase.DEALING ||
      gameState.phase === BlackjackGamePhase.PLAYER_TURN
    ) {
      // Action turn: Only show first card, hide second card
      setDealerCardRevealIndex(1); // 첫 번째 카드만 공개
      setShowDealerTotal(false);
    } else if (
      gameState.phase === BlackjackGamePhase.DEALER_TURN &&
      gameState.dealerCards.length > 0
    ) {
      // 딜러 턴: 두 번째 카드부터 순차 오픈 시작
      setShowDealerTotal(false);

      // 1초 후부터 숨겨진 카드들 순차 오픈
      dealerAnimationRef.current.startDelay = setTimeout(() => {
        let currentIndex = 1; // 두 번째 카드부터 시작
        const totalCards = gameState.dealerCards.length;

        const revealNext = () => {
          if (currentIndex < totalCards) {
            setDealerCardRevealIndex(currentIndex + 1);
            currentIndex++;

            // 마지막 카드인 경우 합산 표시
            if (currentIndex === totalCards) {
              const totalTimeout = setTimeout(() => {
                setShowDealerTotal(true);
              }, 500);
              dealerAnimationRef.current.revealTimeouts.push(totalTimeout);
            } else {
              // 1초 후 다음 카드 오픈
              const nextTimeout = setTimeout(revealNext, 1000);
              dealerAnimationRef.current.revealTimeouts.push(nextTimeout);
            }
          }
        };

        revealNext();
      }, 1000);
    } else if (
      gameState.phase === BlackjackGamePhase.RESULTS ||
      gameState.phase === BlackjackGamePhase.GAME_END
    ) {
      // 결과 단계: 모든 카드 즙시 표시
      setDealerCardRevealIndex(gameState.dealerCards.length);
      setShowDealerTotal(true);
    }

    // cleanup function
    return clearAllTimeouts;
  }, [gameState.phase, gameState.dealerCards.length]);

  // 타이머 효과
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (gameState.timeLeft > 0) {
      intervalId = setInterval(() => {
        dispatch({ type: "DECREMENT_TIME" });
      }, 100);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [gameState.timeLeft]);

  // 베팅 처리
  const handlePlaceBet = () => {
    const maxBet = myPlayer?.chips || 0;
    const minBet = gameState.gameSettings.minBet || 5;
    const isAllInRequired = maxBet > 0 && maxBet <= minBet;
    const finalBetAmount = isAllInRequired ? maxBet : betAmount;

    if (socketService && finalBetAmount > 0 && finalBetAmount <= maxBet) {
      if (isAllInRequired || finalBetAmount >= minBet) {
        socketService.placeBet(finalBetAmount);
        dispatch({ type: "SET_MY_BET", amount: finalBetAmount });
      }
    }
  };

  // Hit 액션 - Simultaneous Actions 적용
  const handleHit = () => {
    const myPlayer = gameState.players.find((p) => p.id === myId);
    const canAct =
      myPlayer &&
      !myPlayer.isStanding &&
      !myPlayer.isBust &&
      !myPlayer.isBlackjack &&
      !myPlayer.isEliminated;

    console.log("HIT button clicked", {
      socketService: !!socketService,
      myPlayer: !!myPlayer,
      myId: myId,
      canAct: canAct,
      isStanding: myPlayer?.isStanding,
      isBust: myPlayer?.isBust,
      isBlackjack: myPlayer?.isBlackjack,
    });

    if (socketService && canAct) {
      console.log("Sending HIT request to server");
      socketService.requestHit();
      setHasPlayerActed(true);
    } else {
      console.log("Cannot hit:", {
        noSocketService: !socketService,
        noMyPlayer: !myPlayer,
        cannotAct: !canAct,
        standing: myPlayer?.isStanding,
        bust: myPlayer?.isBust,
        blackjack: myPlayer?.isBlackjack,
      });
    }
  };

  // Stand 액션 - Simultaneous Actions 적용
  const handleStand = () => {
    const myPlayer = gameState.players.find((p) => p.id === myId);
    const canAct =
      myPlayer &&
      !myPlayer.isStanding &&
      !myPlayer.isBust &&
      !myPlayer.isBlackjack &&
      !myPlayer.isEliminated;

    console.log("STAND button clicked", {
      socketService: !!socketService,
      myPlayer: !!myPlayer,
      myId: myId,
      canAct: canAct,
      isStanding: myPlayer?.isStanding,
      isBust: myPlayer?.isBust,
      isBlackjack: myPlayer?.isBlackjack,
    });

    if (socketService && canAct) {
      console.log("Sending STAND request to server");
      socketService.requestStand();
      setHasPlayerActed(true);
    } else {
      console.log("Cannot stand:", {
        noSocketService: !socketService,
        noMyPlayer: !myPlayer,
        cannotAct: !canAct,
        standing: myPlayer?.isStanding,
        bust: myPlayer?.isBust,
        blackjack: myPlayer?.isBlackjack,
      });
    }
  };

  // Double Down 액션 핸들러
  const handleDoubleDown = () => {
    const myPlayer = gameState.players.find((p) => p.id === myId);
    const canDoubleDown =
      myPlayer &&
      !hasPlayerActed &&
      !myPlayer.isStanding &&
      !myPlayer.isBust &&
      !myPlayer.isBlackjack &&
      myPlayer.cards.length === 2; // 초기 2장만 받은 상태

    if (!canDoubleDown) {
      console.log("Cannot double down");
      return;
    }

    const currentBet = myPlayer.currentBet;
    const availableChips = myPlayer.chips;
    const additionalBet = Math.min(currentBet, availableChips);
    const newTotalBet = currentBet + additionalBet;

    setDoubleDownInfo({
      currentBet,
      additionalBet,
      newTotalBet,
    });
    setIsDoubleDownModalOpen(true);
  };

  // Double Down 확인 핸들러
  const handleDoubleDownConfirm = () => {
    if (socketService) {
      socketService.requestDoubleDown();
      setHasPlayerActed(true);
    }
    setIsDoubleDownModalOpen(false);
    setDoubleDownInfo(null);
  };

  // Surrender 액션 핸들러
  const handleSurrender = () => {
    const myPlayer = gameState.players.find((p) => p.id === myId);
    const canSurrender =
      myPlayer &&
      !hasPlayerActed &&
      !myPlayer.isStanding &&
      !myPlayer.isBust &&
      !myPlayer.isBlackjack &&
      myPlayer.cards.length === 2; // 초기 2장만 받은 상태

    if (!canSurrender) {
      console.log("Cannot surrender");
      return;
    }

    const currentBet = myPlayer.currentBet;
    const returnAmount = Math.floor(currentBet / 2);

    setSurrenderInfo({
      currentBet,
      returnAmount,
    });
    setIsSurrenderModalOpen(true);
  };

  // Surrender 확인 핸들러
  const handleSurrenderConfirm = () => {
    if (socketService) {
      socketService.requestSurrender();
      setHasPlayerActed(true);
    }
    setIsSurrenderModalOpen(false);
    setSurrenderInfo(null);
  };

  // Suit helper functions for card display
  const getSuitIcon = (suit: string) => {
    switch (suit) {
      case "hearts":
        return "♥";
      case "diamonds":
        return "♦";
      case "clubs":
        return "♣";
      case "spades":
        return "♠";
      default:
        return "";
    }
  };

  const getSuitColor = (suit: string) => {
    return suit === "hearts" || suit === "diamonds"
      ? "text-red-500"
      : "text-black";
  };

  // Get current phase display text
  const getPhaseDisplayText = (phase: string) => {
    switch (phase) {
      case BlackjackGamePhase.WAITING:
        return t("Dealerjack.waiting");
      case BlackjackGamePhase.BETTING:
        return t("Dealerjack.betting");
      case BlackjackGamePhase.DEALING:
        return t("Dealerjack.dealing");
      case BlackjackGamePhase.PLAYER_TURN:
        return t("Dealerjack.action");
      case BlackjackGamePhase.DEALER_TURN:
        return t("Dealerjack.dealerTurn");
      case BlackjackGamePhase.RESULTS:
        return t("Dealerjack.resultsCheck");
      default:
        return "";
    }
  };

  // 헤더 렌더링
  const renderHeader = () => (
    <ProductGameHeader
      product={product}
      room={room}
      round={gameState.currentRound}
      leftTime={Math.ceil(gameState.timeLeft)}
      isRuleOpen={isRuleOpen}
      setIsRuleOpen={setIsRuleOpen}
      isSettingOpen={isSettingOpen}
      setIsSettingOpen={setIsSettingOpen}
      t={t}
    >
      {/* Leave Room button */}
      <button
        className="w-full px-4 py-2.5 bg-gray-800 hover:bg-red-600 text-red-400 hover:text-white transition-colors font-semibold flex items-center justify-center "
        onClick={() => onReturnToWaitingRoom()}
      >
        <i className="fas fa-sign-out-alt mr-2" />
        {t("leaveRoom")}
      </button>
    </ProductGameHeader>
  );

  // 현재 페이즈 라벨 렌더링
  const renderPhaseLabel = () => {
    const getPhaseColor = (phase: string) => {
      switch (phase) {
        case BlackjackGamePhase.WAITING:
          return "bg-gray-400";
        case BlackjackGamePhase.BETTING:
          return "bg-yellow-400";
        case BlackjackGamePhase.DEALING:
          return "bg-blue-400";
        case BlackjackGamePhase.PLAYER_TURN:
          return "bg-green-400";
        case BlackjackGamePhase.DEALER_TURN:
          return "bg-purple-400";
        case BlackjackGamePhase.RESULTS:
          return "bg-orange-400";
        default:
          return "bg-blue-400";
      }
    };

    return (
      <div className="fixed top-16 left-4 z-40">
        <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/30 rounded-lg px-3 py-1.5">
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 ${getPhaseColor(
                gameState.phase
              )} rounded-full animate-pulse`}
            ></div>
            <span className="text-white text-sm font-medium">
              {getPhaseDisplayText(gameState.phase)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // 딜러 영역 렌더링
  const renderDealerArea = () => {
    const hasCards = gameState.dealerCards.length > 0;
    const dealerValue =
      gameState.phase === BlackjackGamePhase.DEALER_TURN ||
      gameState.phase === BlackjackGamePhase.RESULTS
        ? gameState.dealerValue
        : gameState.dealerVisibleValue;

    return (
      <div className="flex items-center justify-center py-0 border-b border-green-700/50 gap-4 h-20">
        {/* 딜러 라벨 */}
        <div className="flex items-center">
          <div className="w-6 h-6 bg-gradient-to-br from-yellow-600 to-yellow-800 rounded-full mr-2 flex items-center justify-center">
            <i className="fas fa-user-tie text-white text-xs"></i>
          </div>
          <span className="text-white text-base font-semibold">
            {t("Dealerjack.dealer")}
          </span>
        </div>

        {/* 딜러 카드 - 순차 오픈 애니메이션 */}
        {hasCards && (
          <div className="flex space-x-1">
            {gameState.dealerCards.map((card, index) => {
              // 단계별 카드 공개 로직
              let shouldReveal = false;

              if (
                gameState.phase === BlackjackGamePhase.DEALING ||
                gameState.phase === BlackjackGamePhase.PLAYER_TURN
              ) {
                // 액션 턴: 첫 번째 카드만 공개
                shouldReveal = index === 0;
              } else if (gameState.phase === BlackjackGamePhase.DEALER_TURN) {
                // 딜러 턴: dealerCardRevealIndex만큼 공개
                shouldReveal = index < dealerCardRevealIndex;
              } else if (
                gameState.phase === BlackjackGamePhase.RESULTS ||
                gameState.phase === BlackjackGamePhase.GAME_END
              ) {
                // 결과 단계: 모두 공개
                shouldReveal = true;
              }

              return (
                <div
                  key={`dealer-card-${index}`}
                  className={`w-10 h-14 bg-white rounded shadow-md flex flex-col items-center justify-center border border-gray-200 transition-opacity duration-500 ${
                    shouldReveal
                      ? "scale-100 opacity-100"
                      : "scale-95 opacity-80"
                  }`}
                >
                  {shouldReveal ? (
                    <>
                      <span
                        className={`${getSuitColor(
                          card.suit
                        )} text-sm select-none`}
                      >
                        {getSuitIcon(card.suit)}
                      </span>
                      <span
                        className={`${getSuitColor(
                          card.suit
                        )} text-sm font-bold select-none`}
                      >
                        {card.rank}
                      </span>
                    </>
                  ) : (
                    <span className="text-black text-lg select-none">🂠</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 딜러 총합 - 마지막 카드 오픈 후에만 표시 */}
        {hasCards && (
          <div
            className={`text-white text-base font-bold transition-opacity duration-300 ${
              showDealerTotal ||
              gameState.phase === BlackjackGamePhase.RESULTS ||
              gameState.phase === BlackjackGamePhase.GAME_END
                ? "opacity-100 scale-100"
                : "opacity-0 scale-95"
            }`}
          >
            {showDealerTotal ||
            gameState.phase === BlackjackGamePhase.RESULTS ||
            gameState.phase === BlackjackGamePhase.GAME_END
              ? dealerValue
              : "?"}
          </div>
        )}
      </div>
    );
  };

  // 더블다운 확인 모달 렌더링
  const renderDoubleDownModal = () => {
    if (!isDoubleDownModalOpen || !doubleDownInfo) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-gray-700 shadow-xl">
          <div className="text-center text-white text-lg font-medium mb-4">
            🎯 {t("Dealerjack.doubleDownTitle")}
          </div>

          <div className="text-center text-gray-300 text-sm mb-4">
            {t("Dealerjack.doubleDownExplanation")}
          </div>

          <div className="bg-gray-700 rounded-lg p-4 mb-6">
            <div className="text-gray-300 text-sm mb-2">
              {t("Dealerjack.currentBet")}: {doubleDownInfo.currentBet}
              {t("Dealerjack.chips")}
            </div>
            <div className="text-yellow-400 text-sm mb-2">
              {t("Dealerjack.additionalBet")}: +{doubleDownInfo.additionalBet}
              {t("Dealerjack.chips")}
            </div>
            <div className="border-t border-gray-600 pt-2">
              <div className="text-white font-bold">
                {t("Dealerjack.totalBet")}: {doubleDownInfo.newTotalBet}
                {t("Dealerjack.chips")}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsDoubleDownModalOpen(false);
                setDoubleDownInfo(null);
              }}
              className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 rounded-lg font-medium transition-colors"
            >
              {t("Dealerjack.cancel")}
            </button>
            <button
              onClick={handleDoubleDownConfirm}
              className="flex-1 bg-purple-600 hover:bg-purple-500 text-white py-3 rounded-lg font-medium transition-colors"
            >
              {t("Dealerjack.doubleDownTitle")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 서렌더 확인 모달 렌더링
  const renderSurrenderModal = () => {
    if (!isSurrenderModalOpen || !surrenderInfo) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-gray-700 shadow-xl">
          <div className="text-center text-white text-lg font-medium mb-4">
            🏳️ {t("Dealerjack.surrenderTitle")}
          </div>

          <div className="text-center text-gray-300 text-sm mb-4">
            {t("Dealerjack.surrenderExplanation")}
          </div>

          <div className="bg-gray-700 rounded-lg p-4 mb-6">
            <div className="text-gray-300 text-sm mb-2">
              {t("Dealerjack.currentBet")}: {surrenderInfo.currentBet}
              {t("Dealerjack.chips")}
            </div>
            <div className="text-red-400 text-sm mb-2">
              {t("Dealerjack.loss")}: -
              {surrenderInfo.currentBet - surrenderInfo.returnAmount}
              {t("Dealerjack.chips")}
            </div>
            <div className="border-t border-gray-600 pt-2">
              <div className="text-green-400 font-bold">
                {t("Dealerjack.return")}: +{surrenderInfo.returnAmount}
                {t("Dealerjack.chips")}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsSurrenderModalOpen(false);
                setSurrenderInfo(null);
              }}
              className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 rounded-lg font-medium transition-colors"
            >
              {t("Dealerjack.cancel")}
            </button>
            <button
              onClick={handleSurrenderConfirm}
              className="flex-1 bg-orange-600 hover:bg-orange-500 text-white py-3 rounded-lg font-medium transition-colors"
            >
              {t("Dealerjack.surrenderTitle")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 베팅 모달 렌더링
  const renderBettingModal = () => {
    if (!isBettingModalOpen) return null;

    const maxBet = myPlayer?.chips || 0;
    const minBet = gameState.gameSettings.minBet || 5;
    const isAllInRequired = maxBet > 0 && maxBet <= minBet;
    const availableBetAmount = isAllInRequired
      ? maxBet
      : Math.min(Math.max(betAmount, minBet), maxBet);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full border border-gray-700 shadow-xl">
          <div className="text-center text-white text-lg font-medium mb-4">
            💰 {t("Dealerjack.placeBet")}
          </div>

          {/* 플레이어 칩 정보 */}
          <div className="text-center text-gray-300 text-base mb-4">
            {t("Dealerjack.ownChips")}:
            <span className="text-yellow-400 font-bold text-xl ml-2">
              {myPlayer?.chips || 0}
            </span>
          </div>

          {/* 올인 필요 경고 */}
          {isAllInRequired && (
            <div className="bg-orange-600/20 border border-orange-500 rounded-lg p-3 mb-4">
              <div className="text-orange-400 text-sm text-center font-medium">
                ⚠️ 칩이 부족합니다. 올인({maxBet})만 가능합니다.
              </div>
            </div>
          )}

          {/* 베팅 슬라이더 */}
          <div className="mb-6">
            <input
              type="range"
              min={isAllInRequired ? maxBet : minBet}
              max={Math.max(minBet, maxBet)}
              value={availableBetAmount}
              onChange={(e) => setBetAmount(parseInt(e.target.value))}
              className="w-full h-2 accent-yellow-500"
              disabled={isAllInRequired}
            />
            <div className="flex justify-between text-sm text-gray-400 mt-2">
              <span className="font-medium">
                {isAllInRequired ? maxBet : minBet}
              </span>
              <span
                className={`font-bold text-2xl ${
                  isAllInRequired
                    ? "text-orange-400 animate-pulse"
                    : "text-yellow-400"
                }`}
              >
                {availableBetAmount}
                {isAllInRequired && (
                  <span className="text-xs ml-1">(올인)</span>
                )}
              </span>
              <span className="font-medium">{maxBet}</span>
            </div>
          </div>

          {/* Minimum bet guidance */}
          <div className="text-xs text-gray-400 text-center mb-6">
            {isAllInRequired
              ? `올인 베팅: ${maxBet} ${t("Dealerjack.chips")}`
              : `${t("Dealerjack.minimumBet")}: ${minBet} ${t(
                  "Dealerjack.chips"
                )}`}
          </div>

          {/* 버튼 영역 */}
          <div className="flex gap-3">
            <button
              onClick={() => setIsBettingModalOpen(false)}
              className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 rounded-lg font-medium transition-colors"
            >
              {t("Dealerjack.cancel")}
            </button>
            <button
              onClick={() => {
                if (isAllInRequired) {
                  setBetAmount(maxBet);
                }
                handlePlaceBet();
                setIsBettingModalOpen(false);
              }}
              disabled={
                !myPlayer ||
                maxBet <= 0 ||
                (!isAllInRequired && (betAmount > maxBet || betAmount < minBet))
              }
              className={`flex-1 ${
                isAllInRequired
                  ? "bg-orange-600 hover:bg-orange-700"
                  : "bg-yellow-600 hover:bg-yellow-700"
              } disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors`}
            >
              {isAllInRequired ? `올인 (${maxBet})` : t("Dealerjack.bet")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 스킵 결과 처리 - 서버에서 모든 플레이어가 스킵했을 때 브로드캐스팅으로 모달 닫힘
  const handleSkipResults = () => {
    if (socketService) {
      socketService.requestSkipResults();
    }
  };

  // 라운드 결과 모달 - 딜러잭 전용
  const renderResultsModal = () => {
    if (!gameState.showResults || !gameState.roundResults) return null;

    return (
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-2">
        <div className="bg-gradient-to-r from-slate-800/95 to-slate-700/95 backdrop-blur-sm rounded-xl p-3 max-w-sm w-full max-h-[90vh] overflow-hidden border border-slate-600/30 shadow-lg">
          <div className="mb-3">
            <h3 className="text-lg font-medium text-white text-center flex items-center justify-center gap-2">
              <i className="fas fa-trophy text-yellow-400"></i>
              {t("Dealerjack.roundResults")}
            </h3>
          </div>

          {/* 딜러 카드 섹션 - PlayerList 테마 적용 */}
          <div className="mb-3 bg-gradient-to-r from-yellow-900/50 to-yellow-800/50 backdrop-blur-sm rounded-xl p-2 border border-yellow-600/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fas fa-user-tie text-yellow-400 text-sm"></i>
                <span className="text-white font-medium text-sm">
                  {t("Dealerjack.dealer")}
                </span>
              </div>

              {/* 딜러 카드 */}
              <div className="flex gap-0.5">
                {gameState.dealerCards.map((card, cardIndex) => (
                  <div
                    key={cardIndex}
                    className="w-5 h-7 bg-white rounded border border-gray-300 flex flex-col items-center justify-center text-xs shadow-sm"
                  >
                    <span
                      className={`${getSuitColor(
                        card.suit
                      )} text-xs leading-none`}
                    >
                      {getSuitIcon(card.suit)}
                    </span>
                    <span
                      className={`${getSuitColor(card.suit)} font-bold text-xs`}
                    >
                      {card.rank}
                    </span>
                  </div>
                ))}
              </div>

              <div className="text-white text-sm font-bold">
                {gameState.dealerValue}
              </div>
            </div>
          </div>

          {/* 플레이어 결과 헤더 + 스킵 버튼 */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-white text-sm font-medium flex items-center gap-2">
              <i className="fas fa-users text-gray-400"></i>
              {t("Dealerjack.playerResults")} ({gameState.roundResults.length})
            </div>
            <button
              onClick={handleSkipResults}
              className="px-2 py-1 text-white text-xs rounded transition-colors bg-orange-600 hover:bg-orange-700 flex items-center gap-1"
            >
              <i className="fas fa-forward text-xs"></i>
              {t("Dealerjack.skip")}
            </button>
          </div>

          {/* 플레이어 결과 - 스크롤 가능, PlayerList 테마 적용 */}
          <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
            {gameState.roundResults.map((result) => {
              const player = gameState.players.find(
                (p) => p.id === result.playerId
              );

              if (!player) {
                console.warn(
                  `서버 데이터 문제: Player not found for ID ${result.playerId}`
                );
                return null;
              }

              const isMe = player.id === myId;

              return (
                <div
                  key={result.playerId}
                  className={`
                  bg-gradient-to-r ${
                    isMe
                      ? "from-blue-900/80 to-blue-800/80"
                      : "from-slate-800/80 to-slate-700/80"
                  } backdrop-blur-sm rounded-xl p-2 border ${
                    isMe ? "border-blue-600/30" : "border-slate-600/30"
                  } shadow-lg transition-opacity duration-300
                `}
                >
                  {/* 플레이어 정보 + 결과 */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <i
                        className={`fas fa-gamepad ${
                          isMe ? "text-blue-400" : "text-gray-400"
                        } text-sm`}
                      ></i>
                      <span className="text-white font-medium text-sm truncate">
                        {player.name}
                      </span>
                      {isMe && (
                        <span className="text-blue-400 text-xs">
                          ({t("Dealerjack.me")})
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="text-gray-300 text-xs">
                        {t("Dealerjack.ownChips")}
                      </div>
                      <div className="flex items-center text-yellow-400 font-bold text-sm">
                        <i className="fas fa-coins mr-1 text-xs"></i>
                        <span>{result.finalChips}</span>
                      </div>
                      {/* 승리 보상 표시 */}
                      {result.payout > 0 && (
                        <div className="text-green-400 text-xs font-bold">
                          +{result.payout}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 베팅 및 결과 정보 */}
                  <div className="mb-1">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center text-orange-400">
                        <i className="fas fa-coins mr-1"></i>
                        <span className="font-bold">
                          {t("Dealerjack.betAmount")}: {player.currentBet}
                        </span>
                        {player.isDoubledDown && (
                          <span className="ml-1 text-purple-400 font-bold text-xs">
                            ({t("Dealerjack.doubled")})
                          </span>
                        )}
                      </div>

                      {/* 결과 */}
                      <div className="flex items-center gap-2">
                        {player.isSurrendered && (
                          <span className="text-orange-400 font-bold text-xs">
                            🏳️ {t("Dealerjack.surrendered")}
                          </span>
                        )}
                        {!player.isSurrendered && result.result === "win" && (
                          <span className="text-green-400 font-bold text-xs">
                            {player.isBlackjack
                              ? `🏆 ${t("Dealerjack.blackjackWin")}`
                              : `🏆 ${t("Dealerjack.victory")}`}
                          </span>
                        )}

                        {!player.isSurrendered && result.result === "push" && (
                          <span className="text-yellow-400 font-bold text-xs">
                            🤝 {t("Dealerjack.draw")}
                          </span>
                        )}

                        {!player.isSurrendered && result.result === "lose" && (
                          <span className="text-red-400 font-bold text-xs">
                            😔 {t("Dealerjack.defeat")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 카드와 핸드 값 - PlayerList와 동일한 스타일 */}
                  <div className="flex items-center gap-2">
                    <div className="flex flex-wrap gap-0.5">
                      {player.cards.map((card, cardIndex) => (
                        <div
                          key={cardIndex}
                          className="w-5 h-7 bg-white rounded border border-gray-300 flex flex-col items-center justify-center text-xs shadow-sm"
                        >
                          <span
                            className={`${getSuitColor(
                              card.suit
                            )} text-xs leading-none`}
                          >
                            {getSuitIcon(card.suit)}
                          </span>
                          <span
                            className={`${getSuitColor(
                              card.suit
                            )} font-bold text-xs`}
                          >
                            {card.rank}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* 핸드 값과 상태 - PlayerList와 동일한 스타일 */}
                    <div className="flex items-center text-white text-sm gap-2">
                      <span className="font-bold">
                        {player.isSurrendered ? "-" : player.handValue}
                      </span>
                      {player.isBlackjack && (
                        <span className="text-yellow-400 font-bold text-xs">
                          BlackJack!
                        </span>
                      )}
                      {player.isBust && (
                        <span className="text-red-400 font-bold text-xs">
                          BUST!
                        </span>
                      )}
                      {player.isSurrendered && (
                        <span className="text-orange-400 font-bold text-xs">
                          {t("Dealerjack.surrendered")}
                        </span>
                      )}
                      {player.isDoubledDown &&
                        !player.isBust &&
                        !player.isBlackjack && (
                          <span className="text-purple-400 font-bold text-xs">
                            2X
                          </span>
                        )}
                      {!player.isBust &&
                        !player.isBlackjack &&
                        !player.isSurrendered &&
                        !player.isDoubledDown && (
                          <span
                            className={`text-xs font-bold ${
                              result.result === "win"
                                ? "text-green-400"
                                : result.result === "lose"
                                ? "text-red-400"
                                : "text-yellow-400"
                            }`}
                          >
                            {result.result === "win"
                              ? t("Dealerjack.victory")
                              : result.result === "lose"
                              ? t("Dealerjack.defeat")
                              : t("Dealerjack.draw")}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // 채팅 모달
  const renderChatModal = () => (
    <ChatModal
      isOpen={isChatOpen && gameState.chatEnabled}
      onClose={() => setIsChatOpen(false)}
    >
      <Chatbox
        product={product}
        roomName={room.roomName}
        userList={gameState.players.map((player) => ({
          socketId: `blackjack-${player.id}`,
          defaultNickName: player.name,
          nickName: player.name,
          id: player.id,
          isHost: player.id === gameState.hostId,
          state: "ingame" as any,
          isAI: false,
        }))}
        chatList={messages}
        setChatList={setMessages}
        smallChatKey={"blackjack"}
        isUserListOpen={false}
        setIsUserListOpen={() => {}}
      />
    </ChatModal>
  );

  // 메인 콘텐츠 렌더링
  const renderMainContent = () => {
    if (gameState.phase === BlackjackGamePhase.WAITING) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white mt-10">
            <div>{t("Dealerjack.waitingForGame")}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        {/* Dealer Section - Fixed height */}
        <div className="flex-shrink-0">{renderDealerArea()}</div>

        {/* Players Section - Scrollable */}
        <div className="flex-1 overflow-hidden flex flex-col bg-gradient-to-br from-green-800 via-green-700 to-green-900">
          <div className="text-white text-sm font-medium mb-3 px-4 flex-shrink-0 pt-4">
            <i className="fas fa-users mr-2"></i>
            {t("players")} ({gameState.players.length})
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-8">
            <DealerJackPlayerList
              players={gameState.players}
              myId={myId}
              showCards={
                gameState.phase === BlackjackGamePhase.BETTING ||
                gameState.phase === BlackjackGamePhase.DEALING ||
                gameState.phase === BlackjackGamePhase.PLAYER_TURN ||
                gameState.phase === BlackjackGamePhase.DEALER_TURN ||
                gameState.phase === BlackjackGamePhase.RESULTS
              }
              showBets={true}
              gamePhase={gameState.phase}
              isBettingPhase={gameState.phase === BlackjackGamePhase.BETTING}
              betsRevealed={gameState.phase !== BlackjackGamePhase.BETTING}
              actionsRevealed={
                gameState.phase !== BlackjackGamePhase.PLAYER_TURN
              }
            />
          </div>
        </div>
      </div>
    );
  };

  // 고정 하단 푸터 렌더링
  const renderFixedFooter = () => {
    const isBettingPhase = gameState.phase === BlackjackGamePhase.BETTING;
    const isPlayerTurn = gameState.phase === BlackjackGamePhase.PLAYER_TURN;
    const canAct =
      myPlayer &&
      !myPlayer.isStanding &&
      !myPlayer.isBust &&
      !myPlayer.isBlackjack &&
      !myPlayer.isSurrendered &&
      !myPlayer.isDoubledDown &&
      !myPlayer.isEliminated; // 파산된 플레이어는 액션 불가
    const hasBet = myPlayer && myPlayer.currentBet > 0;

    // 더블다운과 항복은 첫 번째 액션으로만 가능
    const canDoubleDown =
      isPlayerTurn &&
      canAct &&
      !hasPlayerActed &&
      myPlayer &&
      myPlayer.cards.length === 2 &&
      myPlayer.chips >= myPlayer.currentBet; // 더블다운 가능한 칩 보유

    const canSurrender =
      isPlayerTurn &&
      canAct &&
      !hasPlayerActed &&
      myPlayer &&
      myPlayer.cards.length === 2;

    return (
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-600/30 p-4">
        {/* 컴팩트 버튼 영역 - 중앙 정렬 */}
        <div className="action-buttons flex justify-center gap-2 flex-wrap">
          {/* 베팅 단계: 베팅 버튼만 표시 (파산된 플레이어 제외) */}
          {isBettingPhase && !myPlayer?.isEliminated && (
            <button
              onClick={() => setIsBettingModalOpen(true)}
              disabled={hasBet}
              className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <i className="fas fa-coins text-sm"></i>
              <span className="text-base">{t("Dealerjack.bet")}</span>
            </button>
          )}

          {/* 액션 단계: 액션 버튼들만 표시 (파산된 플레이어 제외) */}
          {isPlayerTurn && !myPlayer?.isEliminated && (
            <>
              {/* 더블다운 버튼 */}
              <button
                onClick={handleDoubleDown}
                disabled={!canDoubleDown}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5"
                title={
                  !canDoubleDown
                    ? t("Dealerjack.firstActionOnly")
                    : t("Dealerjack.doubleDownTitle")
                }
              >
                <i className="fas fa-angle-double-up text-xs"></i>
                <span className="text-sm">{t("Dealerjack.doubleDown")}</span>
              </button>

              {/* 항복 버튼 */}
              <button
                onClick={handleSurrender}
                disabled={!canSurrender}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5"
                title={
                  !canSurrender
                    ? t("Dealerjack.firstActionOnly")
                    : t("Dealerjack.surrenderTitle")
                }
              >
                <i className="fas fa-flag text-xs"></i>
                <span className="text-sm">
                  {t("Dealerjack.surrenderTitle")}
                </span>
              </button>

              {/* 히트 버튼 */}
              <button
                onClick={handleHit}
                disabled={!canAct}
                className="hit-button bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5"
              >
                <i className="fas fa-plus text-xs"></i>
                <span className="text-sm">{t("Dealerjack.hit")}</span>
              </button>

              {/* 스탠드 버튼 */}
              <button
                onClick={handleStand}
                disabled={!canAct}
                className="stand-button bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5"
              >
                <i className="fas fa-hand-paper text-xs"></i>
                <span className="text-sm">{t("Dealerjack.stand")}</span>
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // 최종 게임 결과 모달 - 게임 종료 시 표시
  const renderFinalResultsModal = () => {
    if (!gameState.showFinalScores || !gameState.gameEnded) return null;

    const myId = UserSession.getInstance().getUserId();

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-r from-slate-800/95 to-slate-700/95 backdrop-blur-sm rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-hidden border border-slate-600/30 shadow-lg">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-white text-center flex items-center justify-center gap-2">
              <i className="fas fa-trophy text-yellow-400"></i>
              {t("Dealerjack.finalBattleResults")}
            </h3>
          </div>

          {/* 최종 순위 리스트 */}
          <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
            {gameState.finalRankings.map((ranking, index) => {
              const player = gameState.players.find(
                (p) => p.id === ranking.playerId
              );

              if (!player) {
                console.warn(
                  `Player not found for ranking ID ${ranking.playerId}`
                );
                return null;
              }

              const isMe = player.id === myId;
              const isWinner = index === 0;

              return (
                <div
                  key={ranking.playerId}
                  className={`
                    bg-gradient-to-r ${
                      isWinner
                        ? "from-yellow-900/80 to-yellow-800/80 border-yellow-600/50"
                        : isMe
                        ? "from-blue-900/80 to-blue-800/80 border-blue-600/30"
                        : "from-slate-800/80 to-slate-700/80 border-slate-600/30"
                    } backdrop-blur-sm rounded-xl p-4 border shadow-lg transition-opacity duration-300
                  `}
                >
                  <div className="flex items-center justify-between">
                    {/* 순위와 플레이어 정보 */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className={`
                        w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                        ${
                          isWinner
                            ? "bg-yellow-500 text-black"
                            : "bg-gray-600 text-white"
                        }
                      `}
                      >
                        {ranking.rank || index + 1}
                      </div>

                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <i
                          className={`fas fa-gamepad ${
                            isMe ? "text-blue-400" : "text-gray-400"
                          } text-sm`}
                        ></i>
                        <span className="text-white font-medium truncate">
                          {player.name}
                        </span>
                        {isMe && (
                          <span className="text-blue-400 text-xs">
                            ({t("Dealerjack.me")})
                          </span>
                        )}
                        {isWinner && (
                          <span className="text-yellow-400 text-xs font-bold">
                            {t("Dealerjack.battleWinner")}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 최종 칩 수 */}
                    <div className="text-right">
                      <div className="flex items-center text-yellow-400 font-bold text-lg">
                        <i className="fas fa-coins mr-2 text-sm"></i>
                        <span>{ranking.chips || player.chips}</span>
                      </div>
                      <div className="text-xs text-gray-400">
                        {t("Dealerjack.finalChips")}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 하단 버튼 */}
          <div className="flex gap-3">
            <button
              onClick={() => onReturnToWaitingRoom()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <i className="fas fa-home text-sm"></i>
              {t("Dealerjack.backToLobby")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-gray-theme dealerjack-container w-full h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 overflow-hidden">
      {renderHeader()}
      {renderPhaseLabel()}

      {/* {isRuleOpen && <RuleModal isOpen={isRuleOpen} recreationId={6} />} */}

      {/* Main Game Area */}
      <div className="pt-16 pb-20 h-full bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 relative">
        {renderMainContent()}

        {/* 액션 완료 알림 */}
        {actionNotification.show && (
          <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
            <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-600/30 rounded-lg px-4 py-2 shadow-lg">
              <div className="text-white text-sm font-medium flex items-center gap-2">
                <i className="fas fa-info-circle text-blue-400"></i>
                {actionNotification.message}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Chat Button */}
      <ChatFloatButton
        onClick={() => setIsChatOpen(!isChatOpen)}
        isOpen={isChatOpen}
        className="bottom-28 right-6"
      />

      {/* Fixed Footer */}
      {renderFixedFooter()}

      {/* Betting Modal */}
      {renderBettingModal()}

      {/* Double Down Modal */}
      {renderDoubleDownModal()}

      {/* Surrender Modal */}
      {renderSurrenderModal()}

      {renderResultsModal()}
      {renderFinalResultsModal()}
      {renderChatModal()}
    </div>
  );
};

export default DealerJackView;
